import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Plus, Trash2, DollarSign, TrendingUp, TrendingDown, Receipt } from "lucide-react";

const CATEGORIES = [
  { value: "materials", label: "Materials", color: "bg-blue-100 text-blue-700" },
  { value: "labor", label: "Labor", color: "bg-green-100 text-green-700" },
  { value: "subcontractor", label: "Subcontractor", color: "bg-purple-100 text-purple-700" },
  { value: "equipment", label: "Equipment", color: "bg-orange-100 text-orange-700" },
  { value: "other", label: "Other", color: "bg-slate-100 text-slate-700" },
];

export default function JobCostingPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ entries: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newEntry, setNewEntry] = useState({ project_id: "", category: "materials", description: "", amount: 0, vendor: "", date: "" });

  useEffect(() => {
    setNewEntry(e => ({ ...e, project_id: projectId }));
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/job-costing/${projectId}`);
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load cost data");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newEntry.description || !newEntry.amount) { toast.error("Fill description and amount"); return; }
    try {
      await axios.post(`${API}/job-costing`, { ...newEntry, project_id: projectId });
      toast.success("Cost entry added");
      setShowAdd(false);
      setNewEntry({ project_id: projectId, category: "materials", description: "", amount: 0, vendor: "", date: "" });
      fetchData();
    } catch (err) {
      toast.error("Failed to add entry");
    }
  };

  const handleDelete = async (costId) => {
    if (!window.confirm("Delete this cost entry?")) return;
    try {
      await axios.delete(`${API}/job-costing/${costId}`);
      fetchData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const s = data.summary;
  const isProfit = (s.profit || 0) >= 0;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="job-costing-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Job Costing</h1>
        <Button onClick={() => setShowAdd(true)} data-testid="add-cost-btn"><Plus className="w-4 h-4 mr-2" /> Add Cost</Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-5 text-center">
          <p className="text-xs text-slate-500">Estimate Value</p>
          <p className="text-xl font-bold text-slate-800">${(s.estimate_value || 0).toLocaleString()}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 text-center">
          <p className="text-xs text-slate-500">Total Cost</p>
          <p className="text-xl font-bold text-red-600">${(s.total_cost || 0).toLocaleString()}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 text-center">
          <p className="text-xs text-slate-500">Profit</p>
          <p className={`text-xl font-bold ${isProfit ? "text-green-600" : "text-red-600"}`}>
            {isProfit ? <TrendingUp className="w-4 h-4 inline mr-1" /> : <TrendingDown className="w-4 h-4 inline mr-1" />}
            ${Math.abs(s.profit || 0).toLocaleString()}
          </p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 text-center">
          <p className="text-xs text-slate-500">Margin</p>
          <p className={`text-xl font-bold ${(s.margin_percent || 0) >= 0 ? "text-green-600" : "text-red-600"}`}>{s.margin_percent || 0}%</p>
        </CardContent></Card>
      </div>

      {/* By Category */}
      {s.by_category && Object.keys(s.by_category).length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {Object.entries(s.by_category).map(([cat, val]) => {
            const c = CATEGORIES.find(cc => cc.value === cat);
            return <Badge key={cat} className={c?.color || ""}>{c?.label || cat}: ${val.toLocaleString()}</Badge>;
          })}
        </div>
      )}

      {/* Entries */}
      <Card>
        <CardContent className="p-0">
          {data.entries.length > 0 ? (
            <table className="data-table w-full">
              <thead><tr><th>Date</th><th>Category</th><th>Description</th><th>Vendor</th><th>Amount</th><th></th></tr></thead>
              <tbody>
                {data.entries.map(e => {
                  const c = CATEGORIES.find(cc => cc.value === e.category);
                  return (
                    <tr key={e.cost_id}>
                      <td className="text-sm">{e.date}</td>
                      <td><Badge className={c?.color || ""}>{c?.label || e.category}</Badge></td>
                      <td className="font-medium">{e.description}</td>
                      <td className="text-sm text-slate-500">{e.vendor || "—"}</td>
                      <td className="font-semibold text-red-600">${e.amount.toLocaleString()}</td>
                      <td><Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => handleDelete(e.cost_id)}><Trash2 className="w-3 h-3" /></Button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12"><Receipt className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No cost entries yet</p></div>
          )}
        </CardContent>
      </Card>

      {/* Add Modal */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
          <DialogHeader><DialogTitle>Add Cost Entry</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Category</Label>
              <Select value={newEntry.category} onValueChange={(v) => setNewEntry({ ...newEntry, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Description *</Label><Input value={newEntry.description} onChange={(e) => setNewEntry({ ...newEntry, description: e.target.value })} placeholder="e.g., Cabinet doors from supplier" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount ($) *</Label><Input type="number" value={newEntry.amount} onChange={(e) => setNewEntry({ ...newEntry, amount: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Date</Label><Input type="date" value={newEntry.date} onChange={(e) => setNewEntry({ ...newEntry, date: e.target.value })} /></div>
            </div>
            <div><Label>Vendor</Label><Input value={newEntry.vendor} onChange={(e) => setNewEntry({ ...newEntry, vendor: e.target.value })} placeholder="Supplier name" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button><Button onClick={handleAdd}>Add Entry</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
